import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatCurrency,
    formatDateTime,
    formatNumber,
    ToggleGroup,
    LineChart,
    Switch,
    Chart
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    ILineChartProps,
    ChartDataRow,
    ChartConfig
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "LineChart";

const USAGE_CODE = `<Chart
    config={{
        sales: {
            title: "Net sales",
            colorIndex: 1
        }
    }}
    description="2025, excluding VAT"
    note="Year total ₺40.8 M"
    title="Monthly net sales"
>
    <LineChart
        endLabelBehaviour="label"
        categoryKey="month"
        curve="natural"
        data={data}
    />
</Chart>`;

const CURVE_CODE = `<LineChart
    categoryKey="week"
    curve="linear"
    data={data}
/>

<LineChart
    categoryKey="month"
    curve="step"
    data={data}
/>`;

const END_CODE = `<Chart
    config={config}
    isLegendToggleable={true}
    title="Orders by channel"
>
    <LineChart
        endLabelBehaviour="leader"
        categoryKey="month"
        data={data}
    />
</Chart>`;

const GAPS_CODE = `const data = [
    {
        marketplace: null,
        web: 1560,
        month: 0
    },
    {
        marketplace: 730,
        web: null,
        month: 5
    }
];`;

const RANGE_CODE = `const [metric, setMetric] = useState("revenue");

<Chart
    config={config[metric]}
    title="Store performance"
>
    <Chart.Range
        options={[
            {
                title: "Revenue",
                key: "revenue"
            },
            {
                title: "Orders",
                key: "orders"
            }
        ]}
        onChange={setMetric}
        value={metric}
    />
    <LineChart
        valueFormatter={formatters[metric]}
        data={rows[metric]}
        categoryKey="day"
    />
</Chart>`;

const CURVES = [
    "natural",
    "linear",
    "step"
] as const;

const END_LABELS = [
    "none",
    "label",
    "leader"
] as const;

const POINT_LABELS = [
    "none",
    "end",
    "extremes",
    "all"
] as const;

const METRICS = [
    "revenue",
    "orders",
    "basket"
] as const;

const SALES = [
    2570000,
    2490000,
    2870000,
    3030000,
    3170000,
    3410000,
    3120000,
    3550000,
    3740000,
    3880000,
    4390000,
    4620000
];

const CONVERSION = [
    2.14,
    2.38,
    2.31,
    2.76,
    3.08,
    2.92,
    3.37,
    3.21,
    3.58,
    3.49,
    3.86,
    4.12
];

const SEATS = [
    16,
    16,
    20,
    20,
    20,
    24,
    24,
    28,
    28,
    32,
    32,
    36
];

const APP = [
    1180,
    1240,
    1330,
    1390,
    1470,
    1550,
    1500,
    1590,
    1700,
    1770,
    1910,
    2070
];

const WEB = [
    1560,
    1530,
    1570,
    1550,
    1500,
    1480,
    1400,
    1380,
    1340,
    1290,
    1290,
    1260
];

const MARKETPLACE = [
    640,
    680,
    730,
    820,
    900,
    940,
    1050,
    1110,
    1160,
    1200,
    1230,
    1240
];

const NPS = [
    34,
    38,
    29,
    36,
    41,
    45,
    49,
    46
];

const ORDERS = [
    118,
    124,
    131,
    146,
    139,
    121,
    117,
    120,
    127,
    134,
    151,
    142,
    125,
    119,
    123,
    130,
    138,
    155,
    144,
    128,
    122,
    126,
    133,
    141,
    158,
    149,
    131,
    137,
    172,
    154
];

const BASKET = [
    1042,
    1068,
    1096,
    1121,
    1087,
    1035,
    1019,
    1048,
    1074,
    1102,
    1135,
    1093,
    1041,
    1026,
    1055,
    1083,
    1110,
    1142,
    1101,
    1052,
    1038,
    1061,
    1089,
    1117,
    1158,
    1112,
    1064,
    1072,
    984,
    1046
];

const CONVERSION_KEYS = [
    "conversion"
];

const SEATS_KEYS = [
    "seats"
];

const NPS_KEYS = [
    "nps"
];

const VALUE_KEYS = [
    "value"
];

type GapRowType = ILineChartProps["data"][number];

type CurveType = (typeof CURVES)[number];

type EndLabelType = (typeof END_LABELS)[number];

type PointLabelType = (typeof POINT_LABELS)[number];

type MetricType = (typeof METRICS)[number];

const SALES_KEYS = [
    "sales"
];

const CHANNEL_KEYS = [
    "app",
    "web",
    "marketplace"
];

const getIndexRows = (key: string, values: Array<number>): Array<ChartDataRow> => values.map((value, index) => ({
    category: index,
    [key]: value
}));

const getChannelRows = (): Array<GapRowType> => APP.map((app, index) => ({
    marketplace: MARKETPLACE[index] ?? null,
    web: WEB[index] ?? null,
    category: index,
    app
}));

const getGapRows = (): Array<GapRowType> => getChannelRows().map((row, index) => ({
    ...row,
    marketplace: index < 3 ? null : row.marketplace,
    web: index === 5 ? null : row.web
}));

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("lineChartPage.curveTitle"),
        id: "curve"
    },
    {
        title: t("lineChartPage.multiTitle"),
        id: "multi"
    },
    {
        title: t("lineChartPage.dotsTitle"),
        id: "dots"
    },
    {
        title: t("lineChartPage.gapsTitle"),
        id: "gaps"
    },
    {
        title: t("lineChartPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const LineChartPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const [
        curve,
        setCurve
    ] = useState<CurveType>("natural");

    const [
        endLabel,
        setEndLabel
    ] = useState<EndLabelType>("leader");

    const [
        pointLabel,
        setPointLabel
    ] = useState<PointLabelType>("extremes");

    const [
        isShowDots,
        setIsShowDots
    ] = useState(true);

    const [
        metric,
        setMetric
    ] = useState<MetricType>("revenue");

    const chartStyle: CSSProperties = {
        maxWidth: shapeConfigs.contentMaxWidth / 1.5,
        width: "100%"
    };

    const rowStyle: CSSProperties = {
        justifyContent: "center",
        display: "flex",
        width: "100%"
    };

    const getMonth = (value: number | string | null | undefined) => formatDateTime({
        date: new Date(2025, Number(value), 1),
        locale: activeLocale,
        options: {
            month: "short"
        }
    });

    const getWeek = (value: number | string | null | undefined) => formatDateTime({
        date: new Date(2025, 7, 3 + Number(value) * 7),
        locale: activeLocale,
        options: {
            month: "short",
            day: "numeric"
        }
    });

    const getDay = (value: number | string | null | undefined) => formatDateTime({
        date: new Date(2026, 9, 1 + Number(value)),
        locale: activeLocale,
        options: {
            month: "short",
            day: "numeric"
        }
    });

    const getQuarter = (value: number | string | null | undefined) => `${t("lineChartPage.quarter", [
        String(Number(value) % 4 + 1)
    ])} ${Number(value) < 4 ? "2024" : "2025"}`;

    const getMoney = (value: number) => formatCurrency({
        options: {
            maximumFractionDigits: 1,
            notation: "compact"
        },
        locale: activeLocale,
        currency: "TRY",
        value
    });

    const getFullMoney = (value: number) => formatCurrency({
        options: {
            maximumFractionDigits: 0
        },
        locale: activeLocale,
        currency: "TRY",
        value
    });

    const getCount = (value: number) => formatNumber({
        options: {
            maximumFractionDigits: 0
        },
        locale: activeLocale,
        value
    });

    const salesConfig: ChartConfig = {
        sales: {
            title: t("lineChartPage.salesSeries"),
            colorIndex: 1
        }
    };

    const conversionConfig: ChartConfig = {
        conversion: {
            title: t("lineChartPage.conversionSeries"),
            colorIndex: 1
        }
    };

    const seatsConfig: ChartConfig = {
        seats: {
            title: t("lineChartPage.seatsSeries"),
            colorIndex: 1
        }
    };

    const channelConfig: ChartConfig = {
        marketplace: {
            title: t("lineChartPage.marketplace"),
            colorIndex: 3
        },
        app: {
            title: t("lineChartPage.app"),
            colorIndex: 1
        },
        web: {
            title: t("lineChartPage.web"),
            colorIndex: 2
        }
    };

    const npsConfig: ChartConfig = {
        nps: {
            colorIndex: 1,
            title: "NPS"
        }
    };

    const metricRows: Record<MetricType, Array<ChartDataRow>> = {
        revenue: ORDERS.map((orders, index) => ({
            value: orders * (BASKET[index] ?? 0),
            category: index
        })),
        orders: getIndexRows("value", ORDERS),
        basket: getIndexRows("value", BASKET)
    };

    const metricFormatters: Record<MetricType, (value: number) => string> = {
        basket: getFullMoney,
        revenue: getMoney,
        orders: getCount
    };

    const getMetricConfig = (): ChartConfig => ({
        value: {
            title: t(`lineChartPage.metric.${metric}`),
            colorIndex: 1
        }
    });

    const pick = <ItemType extends string>(items: ReadonlyArray<ItemType>, setter: (item: ItemType) => void) => (value: string | Array<string>) => {
        const nextItem = items.find((item) => item === value);

        if(nextItem) {
            setter(nextItem);
        }
    };

    const renderToggle = <ItemType extends string>(title: string, items: ReadonlyArray<ItemType>, value: ItemType, setter: (item: ItemType) => void) => <ToggleGroup
        onValueChange={pick(items, setter)}
        attachBehaviour="attached"
        isClearEnabled={false}
        variant="outline"
        title={title}
        value={value}
        size="small"
    >
        {
            items.map((item) => <ToggleGroup.Item
                title={item}
                value={item}
                key={item}
            />)
        }
    </ToggleGroup>;

    const renderChart = ({
        isLegendToggleable,
        description,
        children,
        config,
        title,
        note
    }: {
        isLegendToggleable?: boolean;
        children: Array<ReactNode>;
        config: ChartConfig;
        description: string;
        title: string;
        note?: string;
    }) => <div
        style={rowStyle}
    >
        <div
            style={chartStyle}
        >
            <Chart
                isLegendToggleable={isLegendToggleable}
                spreadBehaviour="stretch"
                description={description}
                config={config}
                title={title}
                note={note}
            >
                {children}
            </Chart>
        </div>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("lineChartPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("lineChartPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("lineChartPage.overviewCaption")}
            >
                {
                    renderChart({
                        description: t("lineChartPage.salesDescription"),
                        children: [
                            <LineChart
                                categoryTitle={t("lineChartPage.month")}
                                data={getIndexRows("sales", SALES)}
                                categoryFormatter={getMonth}
                                valueFormatter={getMoney}
                                endLabelBehaviour="label"
                                seriesKeys={SALES_KEYS}
                                categoryKey="category"
                                key="chart"
                            />
                        ],
                        title: t("lineChartPage.salesTitle"),
                        note: t("lineChartPage.salesNote"),
                        config: salesConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("lineChartPage.curveTitle")}
            id="curve"
        >
            <Paragraph>
                {t("lineChartPage.curveText")}
            </Paragraph>
            <Preview
                caption={t("lineChartPage.curveCaption")}
            >
                {renderToggle("curve", CURVES, curve, setCurve)}
                {
                    renderChart({
                        description: t("lineChartPage.conversionDescription"),
                        children: [
                            <LineChart
                                data={getIndexRows("conversion", CONVERSION)}
                                valueFormatter={(value) => `${formatNumber({
                                    options: {
                                        maximumFractionDigits: 1
                                    },
                                    locale: activeLocale,
                                    value
                                })} %`}
                                categoryTitle={t("lineChartPage.week")}
                                seriesKeys={CONVERSION_KEYS}
                                categoryFormatter={getWeek}
                                categoryKey="category"
                                plotSize="small"
                                curve={curve}
                                key="chart"
                            />
                        ],
                        title: t("lineChartPage.conversionTitle"),
                        config: conversionConfig
                    })
                }
            </Preview>
            <Paragraph>
                {t("lineChartPage.stepText")}
            </Paragraph>
            <Preview
                caption={t("lineChartPage.stepCaption")}
            >
                {
                    renderChart({
                        description: t("lineChartPage.seatsDescription"),
                        children: [
                            <LineChart
                                categoryTitle={t("lineChartPage.month")}
                                data={getIndexRows("seats", SEATS)}
                                categoryFormatter={getMonth}
                                valueFormatter={getCount}
                                seriesKeys={SEATS_KEYS}
                                categoryKey="category"
                                plotSize="small"
                                curve="step"
                                key="chart"
                            />
                        ],
                        title: t("lineChartPage.seatsTitle"),
                        config: seatsConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={CURVE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("lineChartPage.multiTitle")}
            id="multi"
        >
            <Paragraph>
                {t("lineChartPage.multiText")}
            </Paragraph>
            <Preview
                caption={t("lineChartPage.multiCaption")}
            >
                {renderToggle("endLabelBehaviour", END_LABELS, endLabel, setEndLabel)}
                {
                    renderChart({
                        description: t("lineChartPage.channelDescription"),
                        children: [
                            <LineChart
                                categoryTitle={t("lineChartPage.month")}
                                categoryFormatter={getMonth}
                                endLabelBehaviour={endLabel}
                                valueFormatter={getCount}
                                seriesKeys={CHANNEL_KEYS}
                                data={getChannelRows()}
                                categoryKey="category"
                                key="chart"
                            />
                        ],
                        title: t("lineChartPage.channelTitle"),
                        isLegendToggleable: true,
                        config: channelConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={END_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("lineChartPage.dotsTitle")}
            id="dots"
        >
            <Paragraph>
                {t("lineChartPage.dotsText")}
            </Paragraph>
            <Preview
                caption={t("lineChartPage.dotsCaption")}
            >
                <Switch
                    onCheckedChange={setIsShowDots}
                    isChecked={isShowDots}
                    title="isShowDots"
                />
                {renderToggle("labelBehaviour", POINT_LABELS, pointLabel, setPointLabel)}
                {
                    renderChart({
                        children: [
                            <LineChart
                                categoryTitle={t("lineChartPage.quarterTitle")}
                                data={getIndexRows("nps", NPS)}
                                categoryFormatter={getQuarter}
                                labelBehaviour={pointLabel}
                                valueFormatter={getCount}
                                isShowDots={isShowDots}
                                categoryKey="category"
                                seriesKeys={NPS_KEYS}
                                plotSize="small"
                                curve="linear"
                                key="chart"
                            />
                        ],
                        description: t("lineChartPage.npsDescription"),
                        title: t("lineChartPage.npsTitle"),
                        note: t("lineChartPage.npsNote"),
                        config: npsConfig
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("lineChartPage.gapsTitle")}
            id="gaps"
        >
            <Paragraph>
                {t("lineChartPage.gapsText")}
            </Paragraph>
            <Preview
                caption={t("lineChartPage.gapsCaption")}
            >
                {
                    renderChart({
                        children: [
                            <LineChart
                                categoryTitle={t("lineChartPage.month")}
                                categoryFormatter={getMonth}
                                valueFormatter={getCount}
                                seriesKeys={CHANNEL_KEYS}
                                categoryKey="category"
                                data={getGapRows()}
                                isShowDots={true}
                                key="chart"
                            />
                        ],
                        description: t("lineChartPage.gapsDescription"),
                        title: t("lineChartPage.channelTitle"),
                        isLegendToggleable: true,
                        config: channelConfig
                    })
                }
            </Preview>
            <CodeBlock
                code={GAPS_CODE}
                language="tsx"
            />
            <Callout>
                {t("lineChartPage.gapsNote")}
            </Callout>
        </Section>
        <Section
            title={t("lineChartPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("lineChartPage.rangeText")}
            </Paragraph>
            <Preview
                caption={t("lineChartPage.rangeCaption")}
            >
                {
                    renderChart({
                        children: [
                            <Chart.Range
                                options={METRICS.map((key) => ({
                                    title: t(`lineChartPage.metric.${key}`),
                                    key
                                }))}
                                title={t("lineChartPage.rangeLabel")}
                                onChange={pick(METRICS, setMetric)}
                                value={metric}
                                key="range"
                            />,
                            <LineChart
                                valueFormatter={metricFormatters[metric]}
                                categoryTitle={t("lineChartPage.day")}
                                categoryFormatter={getDay}
                                data={metricRows[metric]}
                                seriesKeys={VALUE_KEYS}
                                categoryKey="category"
                                key="chart"
                            />
                        ],
                        description: t("lineChartPage.storeDescription"),
                        title: t("lineChartPage.storeTitle"),
                        config: getMetricConfig()
                    })
                }
            </Preview>
            <CodeBlock
                code={RANGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("lineChartPage.a11yPlot"),
                    t("lineChartPage.a11yKeyboard"),
                    t("lineChartPage.a11yLive"),
                    t("lineChartPage.a11yHidden"),
                    t("lineChartPage.a11yTable"),
                    t("lineChartPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "Array<Record<string, number | string | null | undefined>>",
                        description: t("lineChartPage.propData"),
                        name: "data"
                    },
                    {
                        description: t("lineChartPage.propCategoryKey"),
                        name: "categoryKey",
                        type: "string"
                    },
                    {
                        description: t("lineChartPage.propSeriesKeys"),
                        type: "Array<string>",
                        name: "seriesKeys"
                    },
                    {
                        type: "\"natural\" | \"linear\" | \"step\"",
                        description: t("lineChartPage.propCurve"),
                        defaultValue: "\"natural\"",
                        name: "curve"
                    },
                    {
                        description: t("lineChartPage.propEndLabelBehaviour"),
                        type: "\"none\" | \"label\" | \"leader\"",
                        name: "endLabelBehaviour",
                        defaultValue: "\"none\""
                    },
                    {
                        type: "\"none\" | \"end\" | \"extremes\" | \"all\"",
                        description: t("lineChartPage.propLabelBehaviour"),
                        defaultValue: "\"none\"",
                        name: "labelBehaviour"
                    },
                    {
                        description: t("lineChartPage.propIsShowDots"),
                        defaultValue: "false",
                        name: "isShowDots",
                        type: "boolean"
                    },
                    {
                        description: t("lineChartPage.propValueFormatter"),
                        type: "(value: number) => string",
                        name: "valueFormatter"
                    },
                    {
                        type: "(value: number | string, index: number) => string",
                        description: t("lineChartPage.propCategoryFormatter"),
                        name: "categoryFormatter"
                    },
                    {
                        description: t("lineChartPage.propCategoryTitle"),
                        name: "categoryTitle",
                        type: "string"
                    },
                    {
                        description: t("lineChartPage.propPlotSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "plotSize"
                    },
                    {
                        description: t("lineChartPage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        type: "boolean"
                    },
                    {
                        description: t("lineChartPage.propTooltipBehaviour"),
                        type: "\"auto\" | \"follow\" | \"pinned\"",
                        name: "tooltipBehaviour",
                        defaultValue: "\"auto\""
                    },
                    {
                        type: "(content: ChartTooltipContent) => ReactNode",
                        description: t("lineChartPage.propRenderTooltip"),
                        name: "renderTooltip"
                    },
                    {
                        description: t("lineChartPage.propOnActiveChange"),
                        type: "(index: number | undefined) => void",
                        name: "onActiveChange"
                    },
                    {
                        description: t("lineChartPage.propSummary"),
                        name: "summary",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("lineChartPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("lineChartPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("lineChartPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default LineChartPage;
